<script setup lang="ts">
import { VatExemptionReason, VatNumberCheckStatus } from "mws-ts-v2-sdk";

import type { VATProductPercentageCostsPrice } from "~/types/ProductCosts";

type Props = VATProductPercentageCostsPrice;

const {
  handlingVatExplanation,
  itemVatExplanation,
  vatNumberCheckStatus,
  costs,
} = defineProps<Props>();

const showVatInformation = computed(() => {
  const isVatReversed =
    itemVatExplanation?.reason === VatExemptionReason.IsVatReversed ||
    handlingVatExplanation?.reason === VatExemptionReason.IsVatReversed;

  return (
    vatNumberCheckStatus === VatNumberCheckStatus.CheckedNotValid ||
    (vatNumberCheckStatus === VatNumberCheckStatus.CheckedAndValid &&
      isVatReversed)
  );
});

const showVatLine = computed(() => {
  return costs?.price !== 0 || showVatInformation.value;
});
</script>

<template>
  <li
    v-if="costs?.currency && showVatLine"
    class="my-2 w-full"
    data-test="product-costs-vat"
  >
    <div class="flex items-center justify-between">
      <p class="flex gap-1">
        {{ $t("product.costs.VAT") }}
        <span
          v-if="percentage && percentage > 0"
          data-test="product-costs-vat-percentage"
        >
          ({{ percentage }}%)
        </span>
      </p>

      <Transition
        name="fade"
        mode="out-in"
      >
        <BaseCurrency
          :key="costs.price"
          data-test="product-costs-vat-price"
          :show-decimals="true"
          :value="costs.price"
          :iso="costs.currency"
        />
      </Transition>
    </div>
    <div v-if="showVatInformation">
      <span
        class="text-sm text-platform-text-2"
        data-test="product-cost-vat-validity-message"
      >
        {{
          vatNumberCheckStatus === VatNumberCheckStatus.CheckedAndValid
            ? $t("product.costs.VATValid")
            : $t("product.costs.VATInvalid")
        }}
      </span>

      <BaseTooltip class="ml-1">
        <p>
          {{
            vatNumberCheckStatus === VatNumberCheckStatus.CheckedAndValid
              ? $t("product.costs.VATReversedInfo")
              : $t("product.costs.VATInvalidInfo")
          }}
        </p>
      </BaseTooltip>
    </div>
  </li>
</template>

<style lang="postcss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
